import { lazy } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';

import { useUser } from '@/components/auth/user-provider';
import { AppShell } from '@/components/layout/app-shell';
import { BookingOverlapReview } from '@/components/booking/booking-overlap-review';
import { PolicyRequirementsPrompt } from '@/components/booking/policy-requirements-prompt';
import { FeatureRouteGate } from '@/components/tenancy/feature-route-gate';
import { tenantPath } from '@/features/tenancy/urls';
import { useBaseAppRoutes } from '@/hooks/use-app-routes';
import { usePluginStatus } from '@/hooks/use-plugin-status';
import {
  TenantGuardedAnyPermission,
  TenantGuardedPage,
  TenantIndexRedirect,
  TenantShell,
  ZeroMembershipScreen
} from '@/pages/tenancy/tenant-shell';

const InviteAcceptancePage = lazy(() => import('@/pages/tenancy/invite-acceptance-page').then(({ InviteAcceptancePage }) => ({ default: InviteAcceptancePage })));
const EntityAdminPage = lazy(() => import('@/pages/tenancy/entity-admin-page').then(({ EntityAdminPage }) => ({ default: EntityAdminPage })));
const PlatformAdminConsole = lazy(() => import('@/pages/tenancy/platform-admin-console').then(({ PlatformAdminConsole }) => ({ default: PlatformAdminConsole })));
const tenantAdminPages = () => import('@/pages/tenancy/tenant-admin-pages');
const AuditPage = lazy(() => tenantAdminPages().then(({ AuditPage }) => ({ default: AuditPage })));
const ElevationsPage = lazy(() => tenantAdminPages().then(({ ElevationsPage }) => ({ default: ElevationsPage })));
const InvitesPage = lazy(() => tenantAdminPages().then(({ InvitesPage }) => ({ default: InvitesPage })));
const MembersPage = lazy(() => tenantAdminPages().then(({ MembersPage }) => ({ default: MembersPage })));
const ModulesPage = lazy(() => tenantAdminPages().then(({ ModulesPage }) => ({ default: ModulesPage })));
const RolesPage = lazy(() => tenantAdminPages().then(({ RolesPage }) => ({ default: RolesPage })));
const SettingsPage = lazy(() => tenantAdminPages().then(({ SettingsPage }) => ({ default: SettingsPage })));

function LegacyBookingRedirect() {
  const user = useUser();
  const location = useLocation();
  const tenant = user?.tenants.find((item) => item.status === 'active') ?? user?.tenants[0];

  if (!tenant) {
    return <Navigate to={user?.isPlatformAdmin ? '/platform-admin' : '/tenant-access'} replace />;
  }

  const suffix = location.pathname === '/' ? '' : location.pathname;
  return <Navigate to={`/t/${tenant.slug}${suffix}${location.search}${location.hash}`} replace />;
}

function LegacyDashboardRedirect() {
  const user = useUser();
  const { tenantSlug } = useParams<{ tenantSlug?: string }>();
  const tenant = user?.tenants.find((item) => item.status === 'active') ?? user?.tenants[0];
  const destinationTenant = tenantSlug ?? tenant?.slug;

  if (!destinationTenant) {
    return <Navigate to={user?.isPlatformAdmin ? '/platform-admin' : '/tenant-access'} replace />;
  }

  return <Navigate to={tenantPath(destinationTenant, '/admin/dashboard')} replace />;
}

export function App() {
  const user = useUser();
  const location = useLocation();
  const routes = useBaseAppRoutes();
  const { isLoading: pluginsLoading, isError: pluginsError } = usePluginStatus();
  const homeRoute = routes.find((route) => route.path === '/');
  const guestRoute = routes.find((route) => route.path === '/guest/visits');
  const bookingRoutes = routes.filter((route) => route.path !== '/' && route.path !== '/guest/visits');

  return (
    <>
    <BookingOverlapReview key={`booking-overlap:${user?.appUserId ?? 'signed-out'}:${location.pathname}`} />
    <PolicyRequirementsPrompt key={`policy-requirements:${user?.appUserId ?? 'signed-out'}:${location.pathname}`} />
    <Routes>
      <Route element={<AppShell />}>
        <Route path="tenant-access" element={<ZeroMembershipScreen />} />
        <Route path="invite/accept" element={<InviteAcceptancePage />} />
        {guestRoute ? <Route path="guest/visits" element={guestRoute.element} /> : null}
        <Route path="platform-admin" element={<PlatformAdminConsole />} />
        <Route path="platform-admin/tenants/:tenantId" element={<PlatformAdminConsole />} />
        <Route path="t/:tenantSlug" element={<TenantShell />}>
          <Route path="tenant" element={<TenantIndexRedirect />} />
          {homeRoute ? <Route index element={homeRoute.element} /> : null}
          <Route path="dashboard" element={<LegacyDashboardRedirect />} />
          {bookingRoutes.map((route) => (
            <Route
              key={`tenant-${route.id}`}
              path={route.path.replace(/^\//, '')}
              element={route.requiredFeature && route.featureAction ? <FeatureRouteGate feature={route.requiredFeature} action={route.featureAction}>{route.additionalFeature ? <FeatureRouteGate feature={route.additionalFeature} action={route.featureAction}>{route.element}</FeatureRouteGate> : route.element}</FeatureRouteGate> : route.element}
            />
          ))}
          <Route path="tenant/members" element={<TenantGuardedPage permission="tenant.members.read"><MembersPage /></TenantGuardedPage>} />
          <Route path="tenant/invites" element={<TenantGuardedPage permission="tenant.invites.read"><InvitesPage /></TenantGuardedPage>} />
          <Route path="tenant/roles" element={<TenantGuardedPage permission="tenant.roles.read"><RolesPage /></TenantGuardedPage>} />
          <Route path="tenant/modules" element={<TenantGuardedPage permission="tenant.modules.manage"><ModulesPage /></TenantGuardedPage>} />
          <Route path="tenant/settings" element={<TenantGuardedPage permission="tenant.settings.read"><SettingsPage /></TenantGuardedPage>} />
          <Route path="tenant/audit" element={<TenantGuardedPage permission="tenant.audit.read"><AuditPage /></TenantGuardedPage>} />
          <Route path="tenant/elevation" element={<TenantGuardedAnyPermission permissions={['tenant.elevation.request', 'tenant.elevation.approve']}><ElevationsPage /></TenantGuardedAnyPermission>} />
          <Route path="tenant/entity" element={<TenantGuardedPage permission="tenant.settings.read"><EntityAdminPage /></TenantGuardedPage>} />
        </Route>
        {routes.filter((route) => route.path !== '/guest/visits').map((route) => (
          <Route key={`legacy-${route.id}`} path={route.path} element={<LegacyBookingRedirect />} />
        ))}
        <Route path="dashboard" element={<LegacyDashboardRedirect />} />
        <Route path="*" element={pluginsLoading ? (
          <div role="status" className="text-muted-foreground p-6 text-sm">Loading application…</div>
        ) : pluginsError ? (
          <div role="alert" className="p-6 text-sm">Application features could not be loaded. Reload this page to try again.</div>
        ) : <Navigate to="/" replace />} />
      </Route>
    </Routes>
    </>
  );
}
